import { SearchOutlined } from '@ant-design/icons';
import { Input, Select } from 'antd';
import cn from 'classnames';
import { useEffect, useState } from 'react';

import { useLeadsRegistryScopedFilters } from '../../../../hooks/useLeadsRegistryFilters';
import {
  LEADS_REGISTRY_SEARCH_TYPE_OPTIONS,
  getLeadsRegistrySearchPlaceholder,
} from '../../../../model/constants';

import type {
  LeadsRegistryFilterScope,
  LeadsRegistrySearchType,
} from '../../../../model/types';
import type { FC } from 'react';

import './LeadsRegistrySearch.scss';

const SEARCH_DEBOUNCE_MS = 500;

interface LeadsRegistrySearchProps {
  filterScope?: LeadsRegistryFilterScope;
}

export const LeadsRegistrySearch: FC<LeadsRegistrySearchProps> = ({
  filterScope = 'registry',
}) => {
  const { filters, onSearchChange, onSearchTypeChange } =
    useLeadsRegistryScopedFilters(filterScope);

  const [inputValue, setInputValue] = useState(filters.search);

  // Sync local input when filters.search changes from outside (e.g. filter reset).
  useEffect(() => {
    setInputValue(filters.search);
  }, [filters.search]);

  // Debounce writing the typed value to the filter store to avoid a SearchLeads per keystroke.
  // The trimmed-value guard inside the timer skips the dispatch when only leading/trailing
  // whitespace changed, preventing a spurious SearchLeads on whitespace-only edits.
  useEffect(() => {
    if (inputValue === filters.search) return;
    const timer = setTimeout(() => {
      if (inputValue.trim() === filters.search.trim()) return;
      onSearchChange(inputValue);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue, filters.search, onSearchChange]);

  return (
    <div
      className={cn('LeadsRegistrySearch', {
        'LeadsRegistrySearch--active': inputValue.trim().length > 0,
      })}
    >
      <Input
        className="LeadsRegistrySearch__input"
        size="small"
        allowClear
        variant="borderless"
        prefix={<SearchOutlined className="LeadsRegistrySearch__icon" />}
        placeholder={getLeadsRegistrySearchPlaceholder(filters.searchType)}
        value={inputValue}
        onChange={({ target: { value } }) => setInputValue(value)}
      />
      <span className="LeadsRegistrySearch__divider" />
      {/* popupMatchSelectWidth={false}: иначе меню наследует ширину селектора
          и длинные варианты («По имени пользователя») в нём обрезаются
          (issue/11135). */}
      <Select<LeadsRegistrySearchType>
        className="LeadsRegistrySearch__type"
        classNames={{ popup: { root: 'LeadsRegistrySearch__type-options' } }}
        size="small"
        variant="borderless"
        popupMatchSelectWidth={false}
        value={filters.searchType}
        options={LEADS_REGISTRY_SEARCH_TYPE_OPTIONS}
        onChange={(value) => onSearchTypeChange(value)}
      />
    </div>
  );
};
